/**
 * FRAMEWORK CONTENT WATCH — the review queue.
 *
 * WHAT THIS SCREEN IS, AND IS NOT
 * A nightly job compares upstream mapping files with what it saw last time and,
 * when one moves, leaves an ITEM here. This screen is where a person decides.
 * It never applies anything: there is no button on it that edits a mapping, and
 * the API behind it has no such route. "Accept" means "I reviewed this and
 * agree"; the catalogue is changed by hand, elsewhere, on purpose.
 *
 * DECISIONS ARE RECORDS
 * A rejection or a recorded difference REQUIRES a reason, and once saved it can
 * be neither edited nor deleted. It is the sentence a customer needs when an
 * auditor asks why we map something differently from ENISA, so decided items
 * are shown with their reason in full, and they stay on the screen for good.
 *
 * TEXT, NEVER HTML
 * Summaries, ids and reasons are rendered as React text nodes.
 *
 * Gate: `org.update` (checked here for the affordance, and again by the API).
 *
 * Route (to be added to App.tsx by the integrator):  /content-watch
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { useContentWatchT, type ContentWatchKey } from '../lib/contentWatchStrings';
import { cn } from '../lib/cn';
import { Button, Card, EmptyState, ErrorNotice, Modal, Pill, Skeleton, type Tone } from '../components/ui';

// ---------------------------------------------------------------------------
//  Transport types (mirror modules/contentwatch/service.ts)
// ---------------------------------------------------------------------------

type Status = 'OPEN' | 'ACCEPTED' | 'REJECTED' | 'DIFFERS';
type Decision = Exclude<Status, 'OPEN'>;
type Filter = 'OPEN' | 'DECIDED' | 'ALL';

interface ReviewListItem {
  id: string;
  kind: 'BASELINE' | 'CHANGE' | 'LICENCE' | 'UNPARSEABLE';
  status: Status;
  detectedAt: string;
  fromHash: string | null;
  toHash: string;
  summary: string;
  decidedAt: string | null;
  decidedBy: string | null;
  decisionNote: string | null;
  source: { key: string; name: string; licence: string };
}

interface ReviewList {
  items: ReviewListItem[];
  counts: { open: number; decided: number };
}

interface DiffGroup {
  key: string;
  ours: boolean;
  added: string[];
  removed: string[];
  changed: Array<{ counterpart: string; from: string; to: string }>;
  present: string[];
  listsTruncated: boolean;
}

interface DiffDoc {
  kind: 'BASELINE' | 'CHANGE' | 'LICENCE' | 'UNPARSEABLE';
  problem?: string;
  sourceLabel?: string;
  targetLabel?: string;
  comparedLabel?: string | null;
  counts?: {
    before: number | null; after: number; added: number; removed: number; changed: number;
    groups: number; groupsWithOurCitation: number;
  };
  groups?: DiffGroup[];
  groupsTruncated?: boolean;
  ourKeysWithoutUpstream?: string[] | null;
}

interface OurCitation {
  matchedKey: string;
  relation: 'SAME' | 'RELATED';
  controlKey: string;
  controlTitle: string;
  framework: string;
  questionCode: string;
  ref: string;
  coverage: string;
  why?: string;
}

interface ReviewDetail extends ReviewListItem {
  diff: DiffDoc;
  affected: OurCitation[];
}

interface SourceRow {
  key: string;
  name: string;
  licence: string;
  isActive: boolean;
  lastCheckedAt: string | null;
  lastChangedAt: string | null;
  lastStatus: string | null;
  lastError: string | null;
  openReviews: number;
}

const NOTE_MIN = 10;
const NOTE_MAX = 4000;

const STATUS_TONE: Record<Status, Tone> = {
  OPEN: 'warning',
  ACCEPTED: 'good',
  REJECTED: 'serious',
  DIFFERS: 'neutral',
};
const KIND_TONE: Record<ReviewListItem['kind'], Tone> = {
  BASELINE: 'neutral',
  CHANGE: 'warning',
  LICENCE: 'critical',
  UNPARSEABLE: 'critical',
};

export const contentWatchKeys = {
  reviews: (f: Filter) => ['content-watch', 'reviews', f] as const,
  review: (id: string) => ['content-watch', 'review', id] as const,
  sources: ['content-watch', 'sources'] as const,
  attribution: ['content-watch', 'attribution'] as const,
};

// ===========================================================================
//  PAGE
// ===========================================================================

export function ContentReviewPage() {
  const { tx } = useContentWatchT();
  const { allows } = useAuth();
  const [filter, setFilter] = useState<Filter>('OPEN');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<{ item: ReviewListItem; decision: Decision } | null>(null);

  const allowed = allows('org.update');

  const list = useQuery({
    queryKey: contentWatchKeys.reviews(filter),
    queryFn: () => api.get<ReviewList>(`/content-watch/reviews?status=${filter}`),
    enabled: allowed,
  });

  if (!allowed) {
    return (
      <div className="oc-page">
        <ErrorNotice message={tx('contentWatch.loadFailed')} />
      </div>
    );
  }

  const tabs: Array<[Filter, number | null]> = [
    ['OPEN', list.data?.counts.open ?? null],
    ['DECIDED', list.data?.counts.decided ?? null],
    ['ALL', list.data ? list.data.counts.open + list.data.counts.decided : null],
  ];

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header>
        <h1 className="oc-h1">{tx('contentWatch.title')}</h1>
        <p className="oc-muted mt-1 max-w-3xl">{tx('contentWatch.subtitle')}</p>
      </header>

      <div className="rounded-xl border border-line bg-surface-sunken px-4 py-3 text-sm text-ink-secondary max-w-3xl">
        {tx('contentWatch.rule')}
      </div>

      <SourcesPanel />

      <div role="tablist" className="flex flex-wrap gap-2">
        {tabs.map(([f, n]) => (
          <button
            key={f}
            role="tab"
            aria-selected={filter === f}
            onClick={() => { setFilter(f); setExpanded(null); }}
            className={cn(
              'rounded-full px-4 min-h-[2.5rem] text-sm font-medium border transition-colors',
              filter === f
                ? 'bg-brand text-white border-brand'
                : 'bg-surface-raised text-ink-secondary border-line hover:bg-surface-sunken',
            )}
          >
            {tx(`contentWatch.tab.${f}` as ContentWatchKey)}
            {n !== null && <span className="ml-1.5 opacity-80">({n})</span>}
          </button>
        ))}
      </div>

      {list.isLoading && (
        <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-28" />)}</div>
      )}
      {list.isError && (
        <ErrorNotice
          message={tx('contentWatch.loadFailed')}
          onRetry={() => void list.refetch()}
          retryLabel={tx('contentWatch.retry')}
        />
      )}

      {list.data && list.data.items.length === 0 && (
        <EmptyState
          title={tx(filter === 'OPEN' ? 'contentWatch.empty.open.title' : 'contentWatch.empty.decided.title')}
          description={tx(filter === 'OPEN' ? 'contentWatch.empty.open.body' : 'contentWatch.empty.decided.body')}
        />
      )}

      <ul className="space-y-3">
        {list.data?.items.map((item) => (
          <li key={item.id}>
            <ReviewCard
              item={item}
              open={expanded === item.id}
              onToggle={() => setExpanded(expanded === item.id ? null : item.id)}
              onDecide={(decision) => setDeciding({ item, decision })}
            />
          </li>
        ))}
      </ul>

      <p className="oc-muted text-xs">{tx('contentWatch.englishNote')}</p>

      {deciding && (
        <DecisionModal
          item={deciding.item}
          decision={deciding.decision}
          onClose={() => setDeciding(null)}
        />
      )}
    </div>
  );
}

// ===========================================================================
//  SOURCES
// ===========================================================================

function SourcesPanel() {
  const { tx, formatDate } = useContentWatchT();
  const { data } = useQuery({
    queryKey: contentWatchKeys.sources,
    queryFn: () => api.get<{ items: SourceRow[] }>('/content-watch/sources'),
  });
  if (!data || data.items.length === 0) return null;

  return (
    <section aria-label={tx('contentWatch.sources')}>
      <h2 className="oc-label">{tx('contentWatch.sources')}</h2>
      <ul className="grid gap-3 md:grid-cols-2">
        {data.items.map((s) => {
          const bad = s.lastStatus && s.lastStatus !== 'OK';
          return (
            <li key={s.key}>
              <Card className="p-3 sm:p-4">
                <p className="text-sm font-medium text-ink">{s.name}</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
                  <span>{tx('contentWatch.source.licence', { licence: s.licence })}</span>
                  <span>
                    {s.lastCheckedAt
                      ? tx('contentWatch.source.lastChecked', { date: formatDate(s.lastCheckedAt) })
                      : tx('contentWatch.source.neverChecked')}
                  </span>
                  {s.lastChangedAt && (
                    <span>{tx('contentWatch.source.lastChanged', { date: formatDate(s.lastChangedAt) })}</span>
                  )}
                  {!s.isActive && <Pill>{tx('contentWatch.source.inactive')}</Pill>}
                  {s.lastStatus && (
                    <Pill tone={bad ? 'critical' : 'good'}>
                      {tx(`contentWatch.source.status.${s.lastStatus}` as ContentWatchKey)}
                    </Pill>
                  )}
                </div>
                {bad && s.lastError && <p className="mt-2 text-xs text-critical break-words">{s.lastError}</p>}
              </Card>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ===========================================================================
//  ONE ITEM
// ===========================================================================

function ReviewCard({ item, open, onToggle, onDecide }: {
  item: ReviewListItem;
  open: boolean;
  onToggle: () => void;
  onDecide: (d: Decision) => void;
}) {
  const { tx, formatDate } = useContentWatchT();
  const decided = item.status !== 'OPEN';

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone={KIND_TONE[item.kind]}>{tx(`contentWatch.kind.${item.kind}` as ContentWatchKey)}</Pill>
            <Pill tone={STATUS_TONE[item.status]}>{tx(`contentWatch.status.${item.status}` as ContentWatchKey)}</Pill>
          </div>
          <p className="text-sm font-semibold text-ink">{item.source.name}</p>
          <p className="text-xs text-ink-muted">
            {tx('contentWatch.detected', { date: formatDate(item.detectedAt) })}
            <span aria-hidden> · </span>
            <span className="font-mono">
              {item.fromHash ? `${item.fromHash.slice(0, 8)} → ` : ''}{item.toHash.slice(0, 8)}
            </span>
          </p>
          <p className="text-sm text-ink-secondary break-words">{item.summary}</p>
        </div>
        <Button size="sm" onClick={onToggle} aria-expanded={open}>
          {tx(open ? 'contentWatch.hideDiff' : 'contentWatch.showDiff')}
        </Button>
      </div>

      {decided && <DecisionRecord item={item} />}
      {open && <ReviewDetailPanel id={item.id} canDecide={!decided} onDecide={onDecide} />}
    </Card>
  );
}

/** The permanent record of a decision — the reason in full, never behind a click. */
function DecisionRecord({ item }: { item: ReviewListItem }) {
  const { tx, formatDate } = useContentWatchT();
  return (
    <div className="mt-3 rounded-xl border border-line bg-surface-sunken px-4 py-3">
      <p className="text-xs text-ink-muted">
        {tx('contentWatch.decided.by', {
          date: item.decidedAt ? formatDate(item.decidedAt) : '',
          name: item.decidedBy ?? '—',
        })}
      </p>
      {item.decisionNote ? (
        <blockquote className="mt-1.5 text-sm text-ink whitespace-pre-wrap break-words">
          {item.decisionNote}
        </blockquote>
      ) : (
        <p className="mt-1.5 text-sm text-ink-muted">{tx('contentWatch.decided.noNote')}</p>
      )}
      <p className="mt-2 text-xs text-ink-muted">{tx('contentWatch.decided.noChange')}</p>
    </div>
  );
}

function ReviewDetailPanel({ id, canDecide, onDecide }: {
  id: string;
  canDecide: boolean;
  onDecide: (d: Decision) => void;
}) {
  const { tx } = useContentWatchT();
  const q = useQuery({
    queryKey: contentWatchKeys.review(id),
    queryFn: () => api.get<ReviewDetail>(`/content-watch/reviews/${encodeURIComponent(id)}`),
  });

  if (q.isLoading) return <Skeleton className="h-40 mt-4" />;
  if (q.isError || !q.data) {
    return (
      <div className="mt-4">
        <ErrorNotice message={tx('contentWatch.detail.loadFailed')} onRetry={() => void q.refetch()} retryLabel={tx('contentWatch.retry')} />
      </div>
    );
  }
  const { diff, affected } = q.data;

  return (
    <div className="mt-4 space-y-5 border-t border-line pt-4">
      {diff.problem && (
        <div>
          <h3 className="text-sm font-semibold text-ink">{tx('contentWatch.detail.problem')}</h3>
          <p className="mt-1 text-sm text-ink-secondary break-words">{diff.problem}</p>
        </div>
      )}

      {diff.counts && (
        <p className="text-sm text-ink-secondary">
          {diff.counts.before === null
            ? tx('contentWatch.detail.countsBaseline', { after: diff.counts.after })
            : tx('contentWatch.detail.counts', {
                before: diff.counts.before, after: diff.counts.after,
                added: diff.counts.added, removed: diff.counts.removed, changed: diff.counts.changed,
              })}
        </p>
      )}

      {diff.groups && (
        <section>
          <h3 className="text-sm font-semibold text-ink">{tx('contentWatch.detail.groups')}</h3>
          {diff.comparedLabel && (
            <p className="text-xs text-ink-muted mt-0.5">
              {tx('contentWatch.detail.groupsHint', { framework: diff.comparedLabel })}
            </p>
          )}
          {diff.groups.length === 0 ? (
            <p className="mt-2 text-sm text-ink-muted">{tx('contentWatch.detail.noGroups')}</p>
          ) : (
            <ul className="mt-2 divide-y divide-line rounded-xl border border-line">
              {diff.groups.map((g) => <GroupRow key={g.key} group={g} />)}
            </ul>
          )}
          {diff.groupsTruncated && (
            <p className="mt-2 text-xs text-ink-muted">{tx('contentWatch.detail.truncatedGroups')}</p>
          )}
        </section>
      )}

      {diff.kind !== 'LICENCE' && diff.kind !== 'UNPARSEABLE' && (
        <section>
          <h3 className="text-sm font-semibold text-ink">{tx('contentWatch.detail.ours')}</h3>
          <p className="text-xs text-ink-muted mt-0.5">{tx('contentWatch.detail.oursHint')}</p>
          {affected.length === 0 ? (
            <p className="mt-2 text-sm text-ink-muted">{tx('contentWatch.detail.oursNone')}</p>
          ) : (
            <ul className="mt-2 divide-y divide-line rounded-xl border border-line">
              {affected.map((c, i) => (
                <li key={`${c.controlKey}-${c.questionCode}-${c.matchedKey}-${i}`} className="px-3 py-2.5 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/catalogue/${encodeURIComponent(c.controlKey)}`}
                      className="font-medium text-ink hover:text-brand hover:underline"
                    >
                      {c.controlTitle}
                    </Link>
                    <Pill tone={c.coverage === 'FULL' ? 'good' : 'warning'}>
                      {tx(c.coverage === 'FULL' ? 'contentWatch.detail.coverageFull' : 'contentWatch.detail.coveragePartial')}
                    </Pill>
                    {c.relation === 'RELATED' && <Pill>{tx('contentWatch.detail.related')}</Pill>}
                  </div>
                  <p className="text-xs text-ink-muted mt-1">
                    <span className="font-mono">{c.questionCode}</span> · {c.ref}
                    <span aria-hidden> · </span>
                    <span className="font-mono">{c.matchedKey}</span>
                  </p>
                  {c.why && <p className="text-xs text-ink-secondary mt-1">{c.why}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {diff.ourKeysWithoutUpstream && diff.ourKeysWithoutUpstream.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-ink">{tx('contentWatch.detail.noUpstream')}</h3>
          <p className="text-xs text-ink-muted mt-0.5">{tx('contentWatch.detail.noUpstreamHint')}</p>
          <p className="mt-2 flex flex-wrap gap-1.5">
            {diff.ourKeysWithoutUpstream.map((k) => (
              <span key={k} className="font-mono text-xs rounded-md bg-ink/[0.06] px-2 py-1">{k}</span>
            ))}
          </p>
        </section>
      )}

      {canDecide && (
        <section className="border-t border-line pt-4">
          <h3 className="text-sm font-semibold text-ink">{tx('contentWatch.decide.heading')}</h3>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            {([
              ['ACCEPTED', 'contentWatch.decide.accept', 'primary'],
              ['REJECTED', 'contentWatch.decide.reject', 'secondary'],
              ['DIFFERS', 'contentWatch.decide.differs', 'secondary'],
            ] as const).map(([d, label, variant]) => (
              <div key={d} className="rounded-xl border border-line p-3 flex flex-col gap-2">
                <Button variant={variant} onClick={() => onDecide(d)}>{tx(label)}</Button>
                <p className="text-xs text-ink-muted">{tx(`${label}.help` as ContentWatchKey)}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function List({ label, tone, items }: { label: string; tone: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <p className="text-xs flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
      <span className={cn('font-medium', tone)}>{label}</span>
      {items.map((x) => <span key={x} className="font-mono rounded bg-ink/[0.06] px-1.5 py-0.5">{x}</span>)}
    </p>
  );
}

function GroupRow({ group: g }: { group: DiffGroup }) {
  const { tx } = useContentWatchT();
  return (
    <li className="px-3 py-2.5 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm font-semibold text-ink">{g.key}</span>
        <Pill tone={g.ours ? 'good' : 'neutral'}>
          {tx(g.ours ? 'contentWatch.detail.cited' : 'contentWatch.detail.notCited')}
        </Pill>
      </div>
      <List label={tx('contentWatch.detail.added')} tone="text-good" items={g.added} />
      <List label={tx('contentWatch.detail.removed')} tone="text-critical" items={g.removed} />
      <List
        label={tx('contentWatch.detail.changed')}
        tone="text-warning"
        items={g.changed.map((c) => `${c.counterpart}: ${c.from} → ${c.to}`)}
      />
      <List label={tx('contentWatch.detail.present')} tone="text-ink-secondary" items={g.present} />
      {g.listsTruncated && <p className="text-xs text-ink-muted">{tx('contentWatch.detail.truncatedLists')}</p>}
    </li>
  );
}

// ===========================================================================
//  DECISION
// ===========================================================================

function DecisionModal({ item, decision, onClose }: {
  item: ReviewListItem;
  decision: Decision;
  onClose: () => void;
}) {
  const { tx } = useContentWatchT();
  const apiError = useApiError();
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const required = decision !== 'ACCEPTED';
  const trimmed = note.trim();
  const tooShort = required && trimmed.length < NOTE_MIN;

  const mutation = useMutation({
    mutationFn: () =>
      api.post<ReviewDetail>(`/content-watch/reviews/${encodeURIComponent(item.id)}/decision`, {
        decision,
        note: trimmed || null,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['content-watch'] });
      onClose();
    },
    onError: () => {
      // A 409 means someone else decided first: refresh so the screen tells the truth.
      void qc.invalidateQueries({ queryKey: ['content-watch'] });
    },
  });

  const label =
    decision === 'ACCEPTED' ? 'contentWatch.decide.accept'
    : decision === 'REJECTED' ? 'contentWatch.decide.reject'
    : 'contentWatch.decide.differs';

  return (
    <Modal
      open
      onClose={mutation.isPending ? () => {} : onClose}
      title={tx(label)}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            {tx('contentWatch.decide.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => mutation.mutate()}
            disabled={tooShort || mutation.isPending || mutation.isSuccess}
          >
            {mutation.isPending ? tx('contentWatch.decide.saving') : tx('contentWatch.decide.confirm')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink-secondary">{item.summary}</p>
      <p className="mt-3 text-sm text-ink-secondary">{tx(`${label}.help` as ContentWatchKey)}</p>

      <div className="mt-4">
        <label className="oc-label" htmlFor="cw-note">
          {tx(required ? 'contentWatch.decide.note' : 'contentWatch.decide.noteOptional')}
          {required && <span className="text-critical ml-0.5" aria-hidden>*</span>}
        </label>
        <textarea
          id="cw-note"
          className="oc-input !min-h-[7rem] py-2"
          maxLength={NOTE_MAX}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-required={required}
        />
        {required && (
          <p className="mt-1 text-xs text-ink-muted">{tx('contentWatch.decide.noteHint', { min: NOTE_MIN })}</p>
        )}
      </div>

      <p className="mt-3 text-xs font-medium text-ink-secondary">{tx('contentWatch.decide.permanent')}</p>
      {mutation.isError && (
        <div className="mt-3">
          <ErrorNotice message={apiError(mutation.error)} />
        </div>
      )}
    </Modal>
  );
}
